import React, { useContext } from "react";
import { ThemeContext } from "../constant/theme";

export const WelcomeCard = ({ styles = {}, text = {}, language = "en", userName = "", title, subtitle, extraContent, children }) => {
  const { theme } = useContext(ThemeContext);

  const getStoredFirstName = () => {
    const stored = language === "ar" ? localStorage.getItem("employeeArabicName") : localStorage.getItem("employeeName");
    return (stored && stored.split(" ")[0]) || userName || "";
  };

  const computeTitle = () => {
    if (title) {
      if (typeof title === "object") return title[language] || title.en || Object.values(title)[0];
      // Ensure title is not empty even if displayName is empty
      const titleStr = String(title || "").trim();
      return titleStr || "Welcome";
    }

    if (text && text[language] && text[language].title) return text[language].title;

    const name = getStoredFirstName();
    const welcomeText = text && text[language] && text[language].welcome ? text[language].welcome : null;
    return welcomeText ? `${welcomeText}, ${name}` : (name || "Welcome");
  };

  const computeSubtitle = () => {
    if (subtitle) {
      if (typeof subtitle === "object") return subtitle[language] || subtitle.en || Object.values(subtitle)[0];
      return String(subtitle || "").trim();
    }
    return null;
  };

  const displayTitle = computeTitle();
  const displaySubtitle = computeSubtitle();

  const defaultCard = {
    background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
    padding: "32px",
    borderRadius: "24px",
    border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    boxShadow: theme === "dark"
      ? "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)"
      : "0 8px 32px rgba(0,0,0,0.1)",
    animation: "slideInDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
    transition: "all 0.3s ease",
    flexWrap: "wrap",
    gap: "24px",
    marginTop: "-43px",
    marginBottom: "32px",
    direction: language === "ar" ? "rtl" : "ltr",
  };

  // Titles arrive from page styles as fixed px sizes (some as large as
  // 40px+). Clamp them fluidly: desktop keeps the exact given size (clamp
  // max), phones scale down instead of wrapping onto three lines.
  const fluidFontSize = (size, minPx) => {
    const value = typeof size === "number" ? `${size}px` : size;
    if (typeof value !== "string" || !/^\d+(\.\d+)?px$/.test(value)) return value;
    return `clamp(${minPx}px, 6vw, ${value})`;
  };

  const mergedStyles = {
    welcomeCard: { ...(styles.welcomeCard || defaultCard), animation: "slideInDown 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)" },
    contentSection: {
      flex: 1,
      minWidth: "250px",
    },
    welcomeSection: { 
      display: "flex", 
      alignItems: "flex-start",
      justifyContent: "space-between",
      width: "100%",
      gap: "24px",
      flexWrap: "wrap",
    },
    welcomeTitle: {
      margin: 0,
      fontWeight: 700,
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      letterSpacing: "-0.5px",
      overflowWrap: "break-word",
      ...(styles.welcomeTitle || {}),
      fontSize: fluidFontSize((styles.welcomeTitle || {}).fontSize || "28px", 22),
    },
    welcomeSubtitle: { 
      margin: "12px 0 0 0", 
      fontSize: "15px", 
      fontWeight: 500,
      color: theme === "dark" ? "#cbd5e1" : "#64748b",
      opacity: 1,
      ...(styles.welcomeSubtitle || {}),
    },
  };

  return (
    <>
      <style>{`
        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes pulse-ring {
          0% {
            transform: scale(0.33);
            opacity: 0.8;
          }
          80%, 100% {
            transform: scale(1.6);
            opacity: 0;
          }
        }
      `}</style>
      <div style={mergedStyles.welcomeCard}>
        <div style={mergedStyles.contentSection}>
          <h1 style={mergedStyles.welcomeTitle}>{displayTitle}</h1>
          {displaySubtitle && <p style={mergedStyles.welcomeSubtitle}>{displaySubtitle}</p>}
          {extraContent && (
            <div style={{ marginTop: "14px", display: "flex", flexWrap: "wrap" }}>
              {extraContent}
            </div>
          )}
        </div>
        {children && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            {children}
          </div>
        )}
      </div>
    </>
  );
};

export default WelcomeCard;